import { Inject, Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpContextToken } from '@angular/common/http';

import { Observable, EMPTY, from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { OAuthService } from 'angular-oauth2-oidc';
import { ConfigService } from '../services/config/config.service';
import { HttpErrorService } from '../services/http-error.service';
import { AppTokenService } from '@synx-cloud/services';
import { DialogService } from '../services/dialog/dialog.service';
import { GENERIC_ERROR_SUPPRESSION_TOKEN } from '../app.module';

@Injectable()
export class TokenInterceptor implements HttpInterceptor
{
    constructor(private oauthService: OAuthService, private configService: ConfigService, private httpErrorService: HttpErrorService,
        private appTokenService: AppTokenService,
        private dialogService: DialogService, @Inject(GENERIC_ERROR_SUPPRESSION_TOKEN) private suppressErrorToken: HttpContextToken<boolean>)
    {
    }

    // Backend calls that need a bearer: DeviceManagement's own API always; the Incidents (CEL_API_URI)
    // backend only in app-token mode (it validates app tokens, which DeviceManagement only holds then).
    private isInScope(url: string): boolean {
        return url.startsWith(this.configService.API_URI)
            || (this.configService.appTokenMode && url.startsWith(this.configService.CEL_API_URI));
    }

    handleResponseError(error, request?, next?): Observable<HttpEvent<any>> {
        // Invalid token error
        if (error.status === 401) {
            // if not an in-scope backend call return error
            if (!request || !this.isInScope(request.url)) { return of(error); }

            // App-token mode: re-mint a fresh app token from the still-valid B2C session and retry once.
            if (this.configService.appTokenMode) {
                return this.appTokenService.mint().pipe(
                    catchError(() => { this.appTokenService.forceB2cRelogin(); return EMPTY; }),
                    switchMap((): Observable<HttpEvent<any>> => {
                        request = this.cloneAndAddHeader(request);
                        return next.handle(request);
                    }));
            }

            // refresh token and retry API call
            return from(this.oauthService.refreshToken()).pipe(
                switchMap((): Observable<HttpEvent<any>> => {
                        request = this.cloneAndAddHeader(request);
                        return next.handle(request);
                }),
                catchError(e => {
                    if (e.status !== 401) {
                        return this.handleResponseError(e);
                    } else {
                        // if fails again logout user
                        this.oauthService.logOut();
                    }
                }));
        }

        // Access denied error
        else if (error.status === 403) {
            // A suppressed background/bulk request (e.g. the config-download poll, which the WAF can 403
            // on a blocked URL) shouldn't force a logout — let the caller handle it. The genuine
            // not-whitelisted logout still fires on normal (non-suppressed) requests.
            if (request && request.context.get(this.suppressErrorToken)) {
                return of(error);
            }
            this.dialogService.inform("User is not whitelisted to use this application, logging out.")
                .subscribe(() => {
                    this.oauthService.logOut();
                });
        }

        if(!request || !request.context.get(this.suppressErrorToken)) {
            this.httpErrorService.handleHttpError(error);
        }
        return of(error);
    }

    cloneAndAddHeader(request: HttpRequest<any>): HttpRequest<any> {
        const token = this.appTokenService.getBearerForApi();
        return request.clone(
            {
                setHeaders:
                    {
                        Authorization: `Bearer ${token}`
                    }
            });
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>>
    {
        if(this.isInScope(request.url))
        {
            request = this.cloneAndAddHeader(request);
        }

        return next.handle(request).pipe(catchError(error => {
                return this.handleResponseError(error, request, next);
         }));
    }
}
